// Third Party
import clsx from "clsx";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";

// MUI
import { Box, Divider, FormControlLabel, Radio, RadioGroup, Stack, SvgIcon, Typography } from "@mui/material";

// Hooks
import { useModelsByMake } from "@/Common/hooks/MakeModels";

// API
import { getModelYears } from "@/Common/api/vehicles";
import { getModelTrims } from "@/Common/api/vehicles/queries";

// Interfaces
import { TSelectValue } from "@/Layouts/components/InputComponents/SmartSelector/interfaces";

// Components
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import SimpleDoubleChecking from "@/Layouts/components/Buttons/SimpleDoubleChecking";
import SimpleTextField from "@/Layouts/components/InputComponents/SimpleTextField";

// Helpers
import { getWildcardModels } from "@/Common/helpers/Brands";
import { getFixedYearsRange, getRealNumberFromSelection, getRelativeYearsRange, isYear } from "../../../../../helpers/yearsUtils";
import { and, or } from "@/Common/helpers/sonar";

// Utils
import { findError } from "../../../utils";
import { TErrors } from "../../../validation";

// Types
import { ISpiffSetupRule } from "../../../../../types/spiffSetup";
import { EActionType, INITIAL_RULE, TAction } from "../../../reducer";

// ICONS
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { ReactComponent as TrashIcon } from "@/assets/icons/common/trash-icon.svg";

const getSelectedYear = (range: string | undefined, year: string) => {
	if (!year || year.length === 0) {
		return "";
	}

	if (!range || range === "fixedRange") {
		return year ? { label: year } : "";
	}

	const yearWord = parseInt(year, 10) >= -1 && parseInt(year, 10) <= 1 ? "Year" : "Years";

	return { label: `${year} ${yearWord}` };
};

const getUpdateSelectedYearAtChangeRange = (year: string) => {
	return (parseInt(year, 10) - moment().year()).toString();
};

const getDisabledOptions = (range: string | undefined, startYear: string, yearsRange: { label: string }[]) => {
	const startYearNumber = parseInt(startYear, 10);
	if (isNaN(startYearNumber)) {
		return [];
	}

	if (!range || range === "fixedRange") {
		return yearsRange
			.filter((option) => !isNaN(parseInt(option.label, 10)))
			.map((option) => parseInt(option.label, 10))
			.filter((year) => year < startYearNumber)
			.map((year) => year.toString());
	}

	return yearsRange
		.map((option) => {
			const realNumber = getRealNumberFromSelection(option);
			if (realNumber === null) {
				return "";
			}

			if (realNumber < startYearNumber) {
				return option.label;
			}
			return "";
		})
		.filter((year) => year.length > 0);
};

const getSelectedStatus = (range: string | undefined, status: { label: string }[]) => {
	if (status.length === 0) {
		return [];
	}

	switch (range) {
		case "fixedRange":
			return status.filter((item) => item.label === "Used");
		case "relativeRange":
			return status.filter((item) => item.label === "New");
		default:
			return statusOptions;
	}
};

const statusOptions = [{ label: "New" }, { label: "Used" }];

interface IEligibilityRuleProps {
	ruleIndex: number;
	rule: ISpiffSetupRule;
	updateEligibilityRequirements: (updatedState: TAction) => void;
	errors: TErrors;
	makesList: { make: string }[];
	isLoadingMakes: boolean;
}

const EligibilityRule = (props: IEligibilityRuleProps) => {
	const { ruleIndex, rule, updateEligibilityRequirements, errors, makesList, isLoadingMakes } = props;

	const { models: modelList, isLoading: loadingModels } = useModelsByMake(rule.make ?? "");

	const { data: makeModelYears, isLoading: loadingMakeModelYear } = useQuery({
		queryKey: ["modelYears", rule.make, rule.model],
		queryFn: async () => {
			const years = await getModelYears(rule.make, rule.model);
			return years
				.map((item: { year: number }): { label: string } => ({ label: item.year.toString() }))
				.sort((a: { label: string }, b: { label: string }) => parseInt(b.label, 10) - parseInt(a.label, 10));
		},
		staleTime: Infinity,
		enabled: and(!!makesList?.length, !!rule.make, !!rule.model),
	});

	const { data: modelTrims, isLoading: loadingModelTrims } = useQuery({
		queryKey: ["modelTrims", rule.make, rule.model],
		queryFn: () => getModelTrims(rule.make, rule.model),
		enabled: and(!!rule.make, !!rule.model),
	});

	const getYearRange = (): { label: string }[] => {
		if (!makeModelYears) {
			return rule.rangeType === "relativeRange" ? getRelativeYearsRange() : getFixedYearsRange();
		}
		return rule.rangeType === "relativeRange" ? getRelativeYearsRange() : makeModelYears;
	};

	const handleChangeMake = (itemSelected: TSelectValue) => {
		if (!itemSelected || !("make" in itemSelected)) {
			return;
		}
		const updatedRule = { ...INITIAL_RULE, make: itemSelected.make, trim: "" } as ISpiffSetupRule;
		updateEligibilityRequirements({ type: EActionType.UPDATE_RULE, payload: { index: ruleIndex, rule: updatedRule } });
	};

	const handleChangeModel = (itemSelected: TSelectValue) => {
		if (!itemSelected || !("model" in itemSelected)) {
			return;
		}
		const updatedRule = { ...rule, model: (itemSelected as { model: string }).model, trim: "" } as ISpiffSetupRule;
		updateEligibilityRequirements({ type: EActionType.UPDATE_RULE, payload: { index: ruleIndex, rule: updatedRule } });
	};

	const handleChangeTrim = (selectValue: TSelectValue) => {
		const updatedRule = { 
			...rule, 
			trim: (selectValue as { trim: string })?.trim ?? ""
		} as ISpiffSetupRule;
		updateEligibilityRequirements({ 
			type: EActionType.UPDATE_RULE, 
			payload: { index: ruleIndex, rule: updatedRule }
		});
	};

	const handleStatusChange = (selectedItems: TSelectValue) => {
		if (!selectedItems || !Array.isArray(selectedItems)) {
			return;
		}

		let updatedRule = { ...rule };

		if (selectedItems.length === 1 && (selectedItems[0] as { label: string }).label === "New") {
			updatedRule = { ...updatedRule, rangeType: "relativeRange" } as ISpiffSetupRule;
			updateEligibilityRequirements({ type: EActionType.UPDATE_RULE, payload: { index: ruleIndex, rule: updatedRule } });
		}

		const selectedStatus = selectedItems.map((item) => (item as { label: string }).label);

		updatedRule = { ...updatedRule, status: selectedStatus } as ISpiffSetupRule;
		updateEligibilityRequirements({ type: EActionType.UPDATE_RULE, payload: { index: ruleIndex, rule: updatedRule } });
	};

	const handleRangeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const updatedRange = event.target.value;

		let { startYear, endYear } = rule;

		if (updatedRange === "relativeRange") {
			if (startYear.length > 0) {
				startYear = getUpdateSelectedYearAtChangeRange(startYear);
			}
			if (endYear.length > 0) {
				endYear = getUpdateSelectedYearAtChangeRange(endYear);
			}
		} else {
			if (startYear.length > 0) {
				startYear = (moment().year() + parseInt(startYear, 10)).toString();
			}
			if (endYear.length > 0) {
				endYear = (moment().year() + parseInt(endYear, 10)).toString();
			}
		}

		const updatedRule = { ...rule, rangeType: updatedRange, startYear, endYear } as ISpiffSetupRule;
		updateEligibilityRequirements({ type: EActionType.UPDATE_RULE, payload: { index: ruleIndex, rule: updatedRule } });
	};

	const handleStartYearChange = (value: TSelectValue) => {
		if (!value || !("label" in value)) {
			return;
		}
		const selectionValue = getRealNumberFromSelection(value as { label: string });

		if (selectionValue === null) {
			return;
		}

		const isValidValueForYear = isYear(selectionValue ?? null);

		let updatedRule = rule;
		const lastEndYearSelected = rule.endYear;
		const newStartYear = selectionValue.toString();
		if (isValidValueForYear) {
			updatedRule = {
				...rule,
				startYear: newStartYear,
				endYear: parseInt(newStartYear, 10) > parseInt(lastEndYearSelected, 10) ? "" : lastEndYearSelected,
			} as ISpiffSetupRule;
			updateEligibilityRequirements({ type: EActionType.UPDATE_RULE, payload: { index: ruleIndex, rule: updatedRule } });
			return;
		}

		updatedRule = {
			...rule,
			startYear: selectionValue.toString(),
			endYear: selectionValue > parseInt(lastEndYearSelected, 10) ? "" : lastEndYearSelected,
		} as ISpiffSetupRule;
		updateEligibilityRequirements({ type: EActionType.UPDATE_RULE, payload: { index: ruleIndex, rule: updatedRule } });
	};

	const handleEndYearChange = (value: TSelectValue) => {
		if (!value || !("label" in value)) {
			return;
		}

		const selectionValue = getRealNumberFromSelection(value as { label: string });

		if (selectionValue === null) {
			return;
		}

		const isValidValueForYear = isYear(selectionValue ?? null);

		let updatedRule = rule;
		if (isValidValueForYear) {
			updatedRule = { ...rule, endYear: selectionValue.toString() } as ISpiffSetupRule;
			updateEligibilityRequirements({ type: EActionType.UPDATE_RULE, payload: { index: ruleIndex, rule: updatedRule } });
			return;
		}

		updatedRule = { ...rule, endYear: selectionValue.toString() } as ISpiffSetupRule;
		updateEligibilityRequirements({ type: EActionType.UPDATE_RULE, payload: { index: ruleIndex, rule: updatedRule } });
	};

	const handleDeleteRule = (index: number) => {
		updateEligibilityRequirements({ type: EActionType.DELETE_RULE, payload: { index } });
	};

	const handleOnChangeStockExpression = (value: string) => {
		const updatedRule = { ...rule, stockExpression: value } as ISpiffSetupRule;
		updateEligibilityRequirements({ type: EActionType.UPDATE_RULE, payload: { index: ruleIndex, rule: updatedRule } });
	};

	return (
		<Box className="flex flex-row items-end justify-start gap-6 min-w-[946px]">
			<Box>
				<SmartSelector
					classes="!w-[150px]"
					tagsVariant="text"
					selectBy="make"
					showBy={"make"}
					loading={isLoadingMakes}
					disabled={isLoadingMakes}
					options={makesList ?? []}
					selected={rule.make ? { make: rule.make } : ""}
					inputProps={{
						placeholder: "Make",
						hideTooltip: true,
					}}
					popperProps={{ className: "!z-[1350]" } as object}
					hideTooltip={true}
					onChangeAction={(value) => handleChangeMake(value)}
					error={!!findError(errors, ["rules", ruleIndex, "make"])}
				/>
			</Box>
			<Box>
				<SmartSelector
					classes="!w-[150px]"
					tagsVariant="text"
					selectBy="model"
					showBy={"model"}
					options={getWildcardModels(modelList, true) || []}
					selected={rule.model ? { model: rule.model } : ""}
					disabled={or(!rule.make, !modelList)}
					loading={loadingModels}
					inputProps={{
						placeholder: "Model",
						hideTooltip: true,
					}}
					popperProps={{ className: "!z-[1350] !w-[250px]" } as object}
					hideTooltip={true}
					onChangeAction={(value) => handleChangeModel(value)}
					error={!!findError(errors, ["rules", ruleIndex, "model"])}
				/>
			</Box>
			<Box>
				<SmartSelector
					classes="!w-[150px]"
					tagsVariant="text"
					selectBy="trim"
					showBy="trim"
					loading={loadingModelTrims}
					disabled={loadingModelTrims}
					options={modelTrims ?? []}
					selected={rule.trim ? { trim: rule.trim } : ""}
					inputProps={{ placeholder: "Trim", hideTooltip: true }}
					popperProps={{ className: "!z-[1350]" } as object}
					hideTooltip={true}
					onChangeAction={(value) => handleChangeTrim(value)}
					error={!!findError(errors, ["rules", ruleIndex, "trim"])}
				/>
			</Box>
			<Box>
				<SmartSelector
					multiple
					multipleSelectAll
					tagsVariant="text"
					classes="!w-[110px]"
					showBy={"label"}
					selectBy="label"
					options={statusOptions}
					selected={rule.status.map((item) => ({ label: item })) ?? getSelectedStatus(rule.rangeType, statusOptions)}
					inputProps={{
						placeholder: "New/Used",
						hideTooltip: true,
					}}
					popperProps={{ className: "!z-[1350]" } as object}
					hideTooltip={true}
					onChangeAction={(value) => handleStatusChange(value)}
					error={!!findError(errors, ["rules", ruleIndex, "status"])}
				/>
			</Box>
			<Box className="flex flex-col min-w-[226px]">
				<RadioGroup
					row
					aria-labelledby="demo-row-radio-buttons-group-label"
					name="row-radio-buttons-group"
					className="gap-6"
					value={rule.rangeType}
					onChange={handleRangeChange}
				>
					<FormControlLabel
						value="relativeRange"
						label="Relative Range"
						control={<Radio size="small" className="!text-primary" />}
						classes={{ label: "!font-sans !text-xs !font-normal !text-default" }}
					/>
					<FormControlLabel
						value="fixedRange"
						label="Fixed Range"
						disabled={rule.status.length === 1 && rule.status[0] === "New"}
						control={<Radio size="small" className="!text-primary" />}
						classes={{
							label: clsx("!font-sans !text-xs !font-normal !text-default", {
								"!text-disabled": rule.status.length === 1 && rule.status[0] === "New",
							}),
						}}
					/>
				</RadioGroup>
				<Stack direction="row" alignItems={"center"} spacing={2}>
					<Box>
						<SmartSelector
							classes="!w-[80px]"
							tagsVariant="text"
							selectBy="label"
							showBy={"label"}
							loading={loadingMakeModelYear}
							options={getYearRange()}
							selected={getSelectedYear(rule.rangeType, rule.startYear)}
							inputProps={{
								placeholder: "Start",
								hideTooltip: true,
							}}
							listBoxProps={{
								sx: { maxHeight: "220px" },
							}}
							popperProps={
								{
									className: "!z-[1350]",
									sx: {
										minWidth: "85px",
									},
								} as object
							}
							hideTooltip={true}
							onChangeAction={(value) => handleStartYearChange(value)}
							error={!!findError(errors, ["rules", ruleIndex, "startYear"])}
						/>
					</Box>
					<Box>
						{rule.rangeType === "relativeRange" ? (
							<Typography className="!font-sans !text-[14.5px] !text-default" noWrap>
								Current Year
							</Typography>
						) : (
							<Divider className="!min-w-2 !color-default" variant="fullWidth" />
						)}
					</Box>
					<Box>
						<SmartSelector
							classes="!w-[80px]"
							tagsVariant="text"
							selectBy="label"
							showBy={"label"}
							loading={loadingMakeModelYear}
							options={getYearRange()}
							selected={getSelectedYear(rule.rangeType, rule.endYear)}
							disabledOptions={getDisabledOptions(rule.rangeType, rule.startYear, getYearRange())}
							inputProps={{
								placeholder: "End",
								hideTooltip: true,
							}}
							listBoxProps={{
								sx: { maxHeight: "220px" },
							}}
							popperProps={
								{
									className: "!z-[1350]",
									sx: {
										minWidth: "85px",
									},
								} as object
							}
							hideTooltip={true}
							onChangeAction={(value) => handleEndYearChange(value)}
							error={!!findError(errors, ["rules", ruleIndex, "endYear"])}
						/>
					</Box>
				</Stack>
			</Box>
			<Box>
				<SimpleTextField
					classes="!w-[120px]"
					editable
					outlined
					text={rule.stockExpression}
					placeholder={"Stock Expression"}
					inputProps={{ maxLength: 120 }}
					onChange={(value) => handleOnChangeStockExpression(value)}
					error={!!findError(errors, ["rules", ruleIndex, "stockExpression"])}
				/>
			</Box>
			<Box className="!ml-[-16px] !mb-[-2px]">
				<SimpleDoubleChecking
					color={"var(--gray-blue-color)"}
					confirmationButtonColor={"var(--gray-blue-color)"}
					icon={<CloseRoundedIcon fontSize="small" className="pt-[2px]" />}
					iconHover={<CloseRoundedIcon fontSize="small" className="pt-[2px]" />}
					confirmationIcon={<SvgIcon className="pl-[5px] pt-[2px]" component={TrashIcon} />}
					onDeleteAction={() => {
						handleDeleteRule(ruleIndex);
					}}
				/>
			</Box>
		</Box>
	);
};

export default EligibilityRule;
